"use client";

import type { ReactNode } from "react";
import { useTranslations } from "use-intl";

import { CloseIcon } from "@/components/appIcons";
import type { AppIcon } from "@/components/appIcons";
import { CountryFlag } from "@/components/country-flag";
import { formatCountry } from "@garimpa/lib/countries";

type FilterChipTone = "primary" | "green";

const TONE_CLASSES: Record<FilterChipTone, string> = {
  primary: "bg-primary/10 text-primary",
  green: "bg-green-600/10 text-green-600",
};

const CLOSE_HOVER_CLASSES: Record<FilterChipTone, string> = {
  primary: "hover:text-primary-foreground",
  green: "hover:text-green-800",
};

interface FilterChipProps {
  icon?: AppIcon;
  iconSlot?: ReactNode;
  tone?: FilterChipTone;
  onClear: () => void;
  children: ReactNode;
}

export function FilterChip({
  icon,
  iconSlot,
  tone = "primary",
  onClear,
  children,
}: FilterChipProps) {
  const Icon = icon;
  const ta = useTranslations("globals.accessibility");

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 px-3 py-1 rounded-full text-sm ${TONE_CLASSES[tone]}`}
    >
      {iconSlot ?? (Icon ? <Icon className="text-[14px]" /> : null)}
      {children}
      <button
        type="button"
        aria-label={ta("removeFilter")}
        onClick={onClear}
        className={CLOSE_HOVER_CLASSES[tone]}
      >
        <CloseIcon className="text-sm" />
      </button>
    </span>
  );
}

/** Flag image if available, fallback icon otherwise. */
export function CountryChipIcon({ code, fallbackIcon }: { code: string; fallbackIcon: AppIcon }) {
  const FallbackIcon = fallbackIcon;

  return (
    <CountryFlag
      code={code}
      decorative
      size="sm"
      fallback={<FallbackIcon className="text-[14px]" />}
    />
  );
}

/** Country chip label: localized country name or raw code. */
export function countryChipLabel(code: string, locale: string): string {
  return formatCountry(code, locale) || code;
}

/** Year-range chip label (decade shorthand / range / single year / open-ended). */
export function formatYearRangeLabel(yearFrom?: number, yearTo?: number): string {
  if (yearFrom && yearTo) {
    if (yearTo - yearFrom === 9 && yearFrom % 10 === 0) {
      return `${yearFrom.toString().slice(-2)}s`;
    }
    return yearFrom === yearTo ? String(yearFrom) : `${yearFrom}–${yearTo}`;
  }
  return yearFrom ? `${yearFrom}+` : `–${yearTo ?? ""}`;
}
